import MultiSelect, { ISelectOption } from '@widgets/multi-select';
import { FC, useEffect, useMemo, useState } from 'react';
import IconResetFilters from '@shared/icons/IconResetFilters';
import DropdownInput from '@widgets/dropdown-input';
import { ISort } from '@entities/util/ISort';
import { DEFAULT_PER_PAGE } from '@widgets/paginator';
import { useHostingStatusOptions } from '@app/queries/options/useHostingStatusOptions';
import { useHostingLocationOptions } from '@app/queries/options/useHostingLocationOptions';
import { useHostingPeriodOptions } from '@app/queries/options/useHostingPeriodOptions';

export interface IHostingListFilter {
  periods: string[];
  statuses: string[];
  commonPower: string;
  locations: string[];
  nominalPower: string;
  actualPower: string;
  percentOfPower: string;
  freePower: string;
  page: number;
  sort: ISort | null;
  perPage: number;
}

const DefaultFilter: IHostingListFilter = {
  periods: [],
  statuses: [],
  commonPower: '',
  locations: [],
  nominalPower: '',
  actualPower: '',
  percentOfPower: '',
  freePower: '',
  page: 1,
  sort: null,
  perPage: DEFAULT_PER_PAGE,
};

interface IProps {
  onChangeFilters: (values: IHostingListFilter) => void;
  filters: IHostingListFilter;
  onReset: () => void;
}

const HostingListFilter: FC<IProps> = ({ onChangeFilters, filters, onReset }) => {
  const periodsOptionsFetcher = useHostingPeriodOptions();
  const statusesOptionsFetcher = useHostingStatusOptions();
  const locationsOptionsFetcher = useHostingLocationOptions();

  const [periodsOptions, setPeriodsOptions] = useState<ISelectOption[]>();
  const [statusesOptions, setStatusesOptions] = useState<ISelectOption[]>();
  const [locationsOptions, setLocationsOptions] = useState<ISelectOption[]>();

  useEffect(() => {
    (async () => {
      const data = await periodsOptionsFetcher.fetch();
      setPeriodsOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await statusesOptionsFetcher.fetch();
      setStatusesOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await locationsOptionsFetcher.fetch();
      setLocationsOptions(data);
    })();
  }, []);

  return (
    <div className="flex gap-[10px] mb-[15px] items-center">
      <div className="cursor-pointer" onClick={() => onReset()}>
        <IconResetFilters />
      </div>
      <MultiSelect
        title="Период"
        value={
          periodsOptions?.length
            ? periodsOptions?.filter(item => filters?.periods?.includes(item.value))
            : []
        }
        width={100}
        isLoading={!periodsOptionsFetcher.isSuccess}
        options={periodsOptions}
        onChange={values =>
          onChangeFilters({ ...filters, periods: values.map(item => item.value) })
        }
      />
      <MultiSelect
        value={
          statusesOptions?.length
            ? statusesOptions?.filter(item => filters?.statuses?.includes(item.value))
            : []
        }
        isLoading={!statusesOptionsFetcher.isSuccess}
        title="Статус"
        options={statusesOptions}
        width={110}
        onChange={values =>
          onChangeFilters({ ...filters, statuses: values.map(item => item.value) })
        }
      />
      <DropdownInput
        value={filters?.commonPower}
        title="Общая мощность"
        width={140}
        onSave={value => onChangeFilters({ ...filters, commonPower: value })}
        showValue={true}
        onlyNumbers={true}
      />
      <MultiSelect
        value={
          locationsOptions?.length
            ? locationsOptions?.filter(item => filters?.locations?.includes(item.value))
            : []
        }
        isLoading={!locationsOptionsFetcher.isSuccess}
        title="Локация"
        options={locationsOptions}
        width={110}
        onChange={values =>
          onChangeFilters({ ...filters, locations: values.map(item => item.value) })
        }
      />
      <DropdownInput
        value={filters?.nominalPower}
        title="Номин. мощность"
        width={140}
        onSave={value => onChangeFilters({ ...filters, nominalPower: value })}
        showValue={true}
        onlyNumbers={true}
      />
      <DropdownInput
        value={filters?.actualPower}
        title="Факт. мощность"
        width={140}
        onSave={value => onChangeFilters({ ...filters, actualPower: value })}
        showValue={true}
        onlyNumbers={true}
      />
      <DropdownInput
        value={filters?.percentOfPower}
        title="% мощности"
        width={120}
        onSave={value => onChangeFilters({ ...filters, percentOfPower: value })}
        showValue={true}
        onlyNumbers={true}
      />
      <DropdownInput
        value={filters?.freePower}
        title="Свободная мощность"
        width={160}
        onSave={value => onChangeFilters({ ...filters, freePower: value })}
        showValue={true}
        onlyNumbers={true}
      />
    </div>
  );
};

export default HostingListFilter;
